/*
  Flexify Theme — product-sticky.js

  Reveals the sticky buy bar only after the real Add to cart button leaves the
  viewport. Uses IntersectionObserver rather than a scroll handler so it costs
  nothing while the customer is scrolling.
*/

class ProductStickyBar extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const sentinel = document.querySelector('[data-sticky-sentinel]');
    if (!sentinel || !('IntersectionObserver' in window)) return;

    this.hidden = false;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        // Only show once the primary button is above the viewport — not when
        // it is still below, before the customer has ever seen it.
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', scrolledPast);
        // The body padding that keeps the bar from covering the footer has to
        // follow the bar, not the page. Added on load, it reserved 84px under
        // the footer for the whole visit — visible as a blank strip before
        // the shopper had scrolled far enough for the bar to appear.
        document.body.classList.toggle('has-sticky-bar', scrolledPast);
        if (scrolledPast) this.reserveSpace();
      },
      { rootMargin: '0px 0px -100% 0px', threshold: 0 }
    );
    this.observer.observe(sentinel);
  }

  /* The space to reserve is the bar's own height, measured. It was a
     hardcoded 84px, which is close on a wide screen and wrong on a narrow one
     where the title wraps and the bar grows. */
  reserveSpace() {
    const h = Math.round(this.getBoundingClientRect().height);
    // Only write when it changes. This value feeds body padding, which
    // changes layout, which a resize handler would see as a reason to
    // measure again.
    if (h > 0 && h !== this.lastHeight) {
      this.lastHeight = h;
      document.documentElement.style.setProperty('--sticky-bar-h', `${h}px`);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.body.classList.remove('has-sticky-bar');
    document.documentElement.style.removeProperty('--sticky-bar-h');
  }
}

if (!customElements.get('product-sticky-bar')) {
  customElements.define('product-sticky-bar', ProductStickyBar);
}
